import { FC, useMemo } from "react";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, Button, Divider, Paper, Stack, Typography } from "@mui/material";
import { colors } from "@rubi/ui";
import { Model } from "survey-core";
import "survey-core/survey-core.min.css";
import { Survey } from "survey-react-ui";

import {
  FeedbackCollectionFixture,
  FeedbackResponseRow,
} from "@/components/feedback/types";

// Body of the feedback detail view. Designed to feel like a single floating
// page inside the SlideoutDrawer — title / last-updated / report link are all
// part of the first Paper card; the survey response is a sibling Paper below.
// The drawer's close affordance is owned by FeedbackListPage (floating X).

export interface FeedbackResponseDetailProps {
  response: FeedbackResponseRow;
  collection: FeedbackCollectionFixture | undefined;
}

// DUMMY: every feedback response links to the same report for the demo. Once the
// API exists this resolves per-response from the feedback → report association.
const ASSOCIATED_REPORT_URL =
  "https://test.portal.tryrubicon.com/rubireportv2/dd7931ca-b0a1-4acf-972c-9982ad1f04a5/report/7c9fae17-262f-4ae3-83b5-53fabe2b8d4e/condensed";

const formatDateTime = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleString();
};

const PAPER_SX = {
  borderRadius: 1,
};

// SurveyJS ships its own light theme, so its card stays white in both modes.
const SURVEY_PAPER_SX = {
  ...PAPER_SX,
  backgroundColor: colors.common.white,
};

const FeedbackResponseDetail: FC<FeedbackResponseDetailProps> = ({
  response,
  collection,
}) => {
  const model = useMemo(() => {
    if (!collection) {
      return null;
    }
    const m = new Model(collection.surveyjs_configuration);
    m.mode = "display";
    m.showNavigationButtons = false;
    m.showCompletedPage = false;
    m.data = response.surveyjs_data;
    return m;
  }, [collection, response]);

  return (
    <Stack spacing={2}>
      <Paper elevation={3} sx={{ ...PAPER_SX, p: 3 }}>
        <Stack spacing={1.5}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: "0.08em" }}
          >
            Feedback #{response.id}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {collection?.name ?? "Unknown survey"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Last updated {formatDateTime(response.time_updated)}
          </Typography>
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        <Button
          variant="outlined"
          endIcon={<OpenInNewIcon />}
          href={ASSOCIATED_REPORT_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          View associated report
        </Button>
      </Paper>

      <Paper
        elevation={3}
        sx={{ ...(model ? SURVEY_PAPER_SX : PAPER_SX), p: 3 }}
      >
        {model ? (
          <Survey model={model} />
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary">
              Survey definition not found.
            </Typography>
          </Box>
        )}
      </Paper>
    </Stack>
  );
};

export default FeedbackResponseDetail;
